import React from 'react';
import { Cancel } from '@material-ui/icons';
import { FormattedMessage } from 'react-intl';
import { alpha, makeStyles } from '@material-ui/core/styles';
import { Box, Grid, IconButton, Typography } from '@material-ui/core';
// custom interfaces
import { CustomTheme } from 'common/ui/interfaces';
import { IPaymentMethodCustomResponseImt } from 'common/interfaces/invoices';
// custom components
import { FieldInfo } from 'common/components';
import { ReactComponent as BarcodeIcon } from 'img/icons/barcode.svg';

import { formatPrice } from 'common/utils';

import inputLabels from 'common/messages/inputLabels';
import invoiceOperatingMessages from 'common/components/InvoiceOperating/messages';

interface IBarcodeCardProps {
  barcodePaymentMethod: IPaymentMethodCustomResponseImt;

  onClose: () => void;
}

const useStyles = makeStyles((theme: CustomTheme) => ({
  card: {
    borderRadius: theme.shape.borderRadius,
    border: `${theme.spacing(0.125)}px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
    width: '100%',
    padding: theme.spacing(1.5),
    gap: theme.spacing(1),
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  closeCross: {
    padding: theme.spacing(0),
    margin: theme.spacing(0),
  },
  barcodeWrapper: {
    display: 'flex',
    '& p+p ': { paddingTop: theme.spacing(0.125) },
  },
  barcode: {
    marginRight: theme.spacing(0.5),

    '& svg': {
      width: theme.spacing(2.5),
      height: theme.spacing(2.5),
    },
  },
}));

const BarcodeCard: React.FC<IBarcodeCardProps> = (props: IBarcodeCardProps): JSX.Element => {
  const classes = useStyles(props);
  const { barcodePaymentMethod, onClose } = props;

  return (
    <Grid container spacing={0} className={classes.card}>
      <Grid item xs={12} className={classes.header}>
        <Typography variant="h4">{barcodePaymentMethod.get('name')}</Typography>
        <IconButton size="small" onClick={onClose}>
          <Cancel />
        </IconButton>
      </Grid>

      <Grid item xs={12}>
        <Box className={classes.barcodeWrapper}>
          <Typography className={classes.barcode} color="textSecondary">
            <BarcodeIcon />
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {barcodePaymentMethod.get('barcode')}
          </Typography>
        </Box>
      </Grid>

      <Grid item md={8} xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <FieldInfo
              label={<FormattedMessage {...inputLabels.amount} />}
              description={formatPrice(barcodePaymentMethod.get('amount'))}
            />
          </Grid>

          <Grid item xs={4}>
            <FieldInfo
              label={<FormattedMessage {...inputLabels.left} />}
              description={formatPrice(
                barcodePaymentMethod.get('leftAmount') ?? barcodePaymentMethod.get('amount'),
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <FieldInfo
              label={<FormattedMessage {...invoiceOperatingMessages.maxInvoice} />}
              description={`${barcodePaymentMethod.getIn([
                'paymentMethod',
                'invoiceAmountPercentage',
              ])}%`}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default BarcodeCard;
